import React, {FC, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {downloadBulkTemplate} from '../core/_requests'
import {UserModel} from '../../../auth'

type IProps = {
  id: string
  hasError?: boolean
  errorMsg?: string
  templateUrl: string
  token?: string
  user: UserModel | undefined
  isDownloadable?: boolean
  setField: React.Dispatch<any>
  validator?: (file: File) => Promise<string | null>
}

// Excel only — mirrors the accept list used by the shared BulkUploadField.
const EXCEL_ACCEPT =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'

/**
 * Bulk Sheet card for the Add Products - Bulk redesign. The file-handling,
 * optional validator and template-download logic are the same as the shared
 * BulkUploadField; only the presentation differs so the shared component stays
 * untouched for the other bulk views.
 */
const BulkSheetCard: FC<IProps> = ({
  id,
  hasError,
  errorMsg,
  templateUrl,
  token = '',
  user,
  isDownloadable,
  setField,
  validator,
}) => {
  const {formatMessage} = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [customError, setCustomError] = useState<string | null>(null)
  const [downloadErr, setDownloadErr] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const getTemplate = async () => {
    setDownloadErr(null)
    try {
      await downloadBulkTemplate(
        templateUrl,
        token,
        `${user?.name}_${new Date().toISOString().slice(0, 10)}.xlsx`,
      )
    } catch {
      // Its own state rather than customError, which marks the UPLOAD dropzone
      // invalid — the template download failing says nothing about the file
      // the supplier is about to pick.
      setDownloadErr(formatMessage({id: 'template_download_failed'}))
    }
  }

  const acceptFile = async (file?: File | null) => {
    if (!file) {
      setFileName(null)
      setField({error: true, value: ''})
      return
    }
    setFileName(file.name)

    if (validator) {
      try {
        const validationMessage = await validator(file)
        if (validationMessage) {
          setCustomError(validationMessage)
          setField({error: true, value: ''})
          return
        }
      } catch (err) {
        setCustomError(formatMessage({id: 'please_upload_bulk'}))
        setField({error: true, value: ''})
        return
      }
    }

    setCustomError(null)
    setField({error: false, value: file})
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.currentTarget.files?.[0] ?? null)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    acceptFile(e.dataTransfer.files?.[0] ?? null)
  }

  const openPicker = () => inputRef.current?.click()
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      openPicker()
    }
  }

  const invalid = Boolean(customError || hasError)

  return (
    <>
      <div
        role='button'
        tabIndex={0}
        className={`apb-dropzone${isDragOver ? ' is-dragover' : ''}${invalid ? ' is-invalid' : ''}`}
        onClick={openPicker}
        onKeyDown={handleKeyDown}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragOver(true)
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        <div className='apb-dropzone-icon apb-dropzone-icon--primary'>
          <i className='fas fa-file-arrow-up' aria-hidden='true' />
        </div>
        <p className='apb-dropzone-title'>{formatMessage({id: 'upload_from_computer'})}</p>
        <p className='apb-dropzone-hint'>{formatMessage({id: 'bulk_upload_hint'})}</p>
        {fileName && <p className='apb-dropzone-file'>{fileName}</p>}
        <input ref={inputRef} type='file' id={id} accept={EXCEL_ACCEPT} onChange={handleInputChange} />
      </div>

      <div className='apb-sheet-footer'>
        <div className='apb-template-line'>
          {formatMessage({id: 'download_bulk_template'})}{' '}
          {isDownloadable ? (
            <a
              className='apb-template-link'
              href={templateUrl}
              download={`${user?.name}_${new Date().toISOString().slice(0, 10)}`}
            >
              {formatMessage({id: 'from_here'})}
            </a>
          ) : (
            <button type='button' className='apb-template-link' onClick={getTemplate}>
              {formatMessage({id: 'from_here'})}
            </button>
          )}
        </div>
        {downloadErr && (
          <span className='apb-error' role='alert'>
            {downloadErr}
          </span>
        )}
        <div className='apb-warn-amber'>
          <i className='fas fa-triangle-exclamation' aria-hidden='true' />
          <span>{formatMessage({id: 'new_bulk_template'})}</span>
        </div>
        {invalid && (
          <span className='apb-error'>
            {customError || errorMsg || formatMessage({id: 'please_upload_bulk'})}
          </span>
        )}
      </div>
    </>
  )
}

export default BulkSheetCard
